import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Card, Button, Input, Loading } from '../components/common';
import ResumeUploader from '../components/ResumeUploader';
import { getResume, updateResume, uploadResume } from '../services/resumeService';

const ResumeEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resume, setResume] = useState(null);
  const [activeSection, setActiveSection] = useState('contact');

  useEffect(() => {
    if (!isNew) {
      fetchResume();
    }
  }, [id, isNew]);

  const fetchResume = async () => {
    try {
      const response = await getResume(id);
      if (response.success) {
        setResume(response.data.resume);
      }
    } catch (err) {
      toast.error('Failed to load resume');
      navigate('/resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const response = await uploadResume(file);
      if (response.success) {
        const { track } = await import('../utils/analytics').then(m => ({ track: m.analytics.track }));
        track('resume_uploaded');
        toast.success('Resume parsed successfully!');
        navigate(`/resumes/${response.data.resume.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateResume(id, resume);
      if (response.success) {
        toast.success('Resume saved');
        setResume(response.data.resume);
      }
    } catch (err) {
      toast.error('Failed to save resume');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setResume(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const sections = [
    { id: 'contact', label: 'Contact Info' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'education', label: 'Education' },
    { id: 'skills', label: 'Skills' },
    { id: 'projects', label: 'Projects' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loading size="lg" text="Loading resume..." />
      </div>
    );
  }

  // New resume upload view
  if (isNew) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/resumes')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Upload Resume</h1>
            <p className="text-gray-600">
              Upload a PDF and we&apos;ll parse it with AI
            </p>
          </div>
        </div>

        <Card>
          <ResumeUploader onUpload={handleUpload} loading={uploading} />
        </Card>

        <div className="text-center text-sm text-gray-500">
          <p>Your resume will be analyzed by AI to extract:</p>
          <p className="mt-1">Contact info, experience, education, skills, and more</p>
        </div>
      </div>
    );
  }

  // Edit existing resume
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/resumes')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {resume?.name || 'Edit Resume'}
            </h1>
            <p className="text-gray-600">
              Edit your resume details
            </p>
          </div>
        </div>
        <Button onClick={handleSave} loading={saving}>
          <CheckIcon className="h-5 w-5 mr-2" />
          Save Changes
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Section navigation */}
        <div className="w-48 flex-shrink-0">
          <nav className="space-y-1 sticky top-4">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === section.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Section content */}
        <div className="flex-1">
          {activeSection === 'contact' && (
            <Card title="Contact Information">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  value={resume?.contactInfo?.fullName || ''}
                  onChange={(e) => updateField('contactInfo.fullName', e.target.value)}
                />
                <Input
                  label="Email"
                  type="email"
                  value={resume?.contactInfo?.email || ''}
                  onChange={(e) => updateField('contactInfo.email', e.target.value)}
                />
                <Input
                  label="Phone"
                  value={resume?.contactInfo?.phone || ''}
                  onChange={(e) => updateField('contactInfo.phone', e.target.value)}
                />
                <Input
                  label="Location"
                  value={resume?.contactInfo?.location || ''}
                  onChange={(e) => updateField('contactInfo.location', e.target.value)}
                />
                <Input
                  label="LinkedIn"
                  value={resume?.contactInfo?.linkedin || ''}
                  onChange={(e) => updateField('contactInfo.linkedin', e.target.value)}
                />
                <Input
                  label="Portfolio"
                  value={resume?.contactInfo?.portfolio || ''}
                  onChange={(e) => updateField('contactInfo.portfolio', e.target.value)}
                />
                <Input
                  label="GitHub"
                  value={resume?.contactInfo?.github || ''}
                  onChange={(e) => updateField('contactInfo.github', e.target.value)}
                />
              </div>
            </Card>
          )}

          {activeSection === 'summary' && (
            <Card title="Professional Summary">
              <textarea
                className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Write a brief professional summary..."
                value={resume?.summary || ''}
                onChange={(e) => updateField('summary', e.target.value)}
              />
              <p className="mt-2 text-sm text-gray-500">
                A 2-3 sentence summary of your professional background and goals.
              </p>
            </Card>
          )}

          {activeSection === 'experience' && (
            <Card title="Work Experience">
              {resume?.experience?.length > 0 ? (
                <div className="space-y-6">
                  {resume.experience.map((exp, index) => (
                    <div key={exp.id || index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <Input
                          label="Company"
                          value={exp.company || ''}
                          onChange={(e) => {
                            const updated = [...resume.experience];
                            updated[index] = { ...updated[index], company: e.target.value };
                            updateField('experience', updated);
                          }}
                        />
                        <Input
                          label="Title"
                          value={exp.title || ''}
                          onChange={(e) => {
                            const updated = [...resume.experience];
                            updated[index] = { ...updated[index], title: e.target.value };
                            updateField('experience', updated);
                          }}
                        />
                      </div>
                      <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Achievements & Responsibilities
                        </label>
                        <textarea
                          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          value={exp.bullets?.join('\n') || ''}
                          onChange={(e) => {
                            const updated = [...resume.experience];
                            updated[index] = {
                              ...updated[index],
                              bullets: e.target.value.split('\n').filter(Boolean)
                            };
                            updateField('experience', updated);
                          }}
                          placeholder="One bullet point per line..."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No work experience added yet
                </p>
              )}
            </Card>
          )}

          {activeSection === 'education' && (
            <Card title="Education">
              {resume?.education?.length > 0 ? (
                <div className="space-y-4">
                  {resume.education.map((edu, index) => (
                    <div key={edu.id || index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="grid md:grid-cols-2 gap-4">
                        <Input
                          label="Institution"
                          value={edu.institution || ''}
                          onChange={(e) => {
                            const updated = [...resume.education];
                            updated[index] = { ...updated[index], institution: e.target.value };
                            updateField('education', updated);
                          }}
                        />
                        <Input
                          label="Degree"
                          value={edu.degree || ''}
                          onChange={(e) => {
                            const updated = [...resume.education];
                            updated[index] = { ...updated[index], degree: e.target.value };
                            updateField('education', updated);
                          }}
                        />
                        <Input
                          label="Field of Study"
                          value={edu.field || ''}
                          onChange={(e) => {
                            const updated = [...resume.education];
                            updated[index] = { ...updated[index], field: e.target.value };
                            updateField('education', updated);
                          }}
                        />
                        <Input
                          label="GPA"
                          value={edu.gpa || ''}
                          onChange={(e) => {
                            const updated = [...resume.education];
                            updated[index] = { ...updated[index], gpa: e.target.value };
                            updateField('education', updated);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No education added yet
                </p>
              )}
            </Card>
          )}

          {activeSection === 'skills' && (
            <Card title="Skills">
              {resume?.skills?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {resume.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                    >
                      {skill.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No skills added yet
                </p>
              )}
              <div className="mt-4">
                <Input
                  label="Add skills (comma separated)"
                  placeholder="JavaScript, React, Node.js..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const newSkills = e.target.value.split(',').map(s => ({
                        name: s.trim(),
                        category: 'technical'
                      })).filter(s => s.name);
                      updateField('skills', [...(resume.skills || []), ...newSkills]);
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </Card>
          )}

          {activeSection === 'projects' && (
            <Card title="Projects">
              {resume?.projects?.length > 0 ? (
                <div className="space-y-4">
                  {resume.projects.map((proj, index) => (
                    <div key={proj.id || index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="grid md:grid-cols-2 gap-4">
                        <Input
                          label="Project Name"
                          value={proj.name || ''}
                          onChange={(e) => {
                            const updated = [...resume.projects];
                            updated[index] = { ...updated[index], name: e.target.value };
                            updateField('projects', updated);
                          }}
                        />
                        <Input
                          label="URL"
                          value={proj.url || ''}
                          onChange={(e) => {
                            const updated = [...resume.projects];
                            updated[index] = { ...updated[index], url: e.target.value };
                            updateField('projects', updated);
                          }}
                        />
                      </div>
                      <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          className="w-full h-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          value={proj.description || ''}
                          onChange={(e) => {
                            const updated = [...resume.projects];
                            updated[index] = { ...updated[index], description: e.target.value };
                            updateField('projects', updated);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No projects added yet
                </p>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeEditorPage;
